import { User, Bell, Lock, HelpCircle, FileText, LogOut, ChevronRight, Settings, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const { preferences } = useUserPreferences();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  // Get description for display settings
  const getDisplayDescription = () => {
    const langLabel = i18n.language === 'en' ? 'EN' : 'ID';
    const sepLabel = preferences.thousandSeparator === '.' ? '1.000' : '1,000';
    return `${langLabel} • ${preferences.currency} • ${sepLabel}`;
  };

  const menuItems = [
    {
      icon: User,
      label: t('profile.settings'),
      description: t('profile.display_desc'),
      onClick: () => navigate('/profile/edit'),
      visible: false, // Hidden - Task 008 requirement update
    },
    {
      icon: Settings,
      label: t('profile.display'),
      description: getDisplayDescription(),
      onClick: () => navigate('/profile/display'),
      visible: true,
    },
    {
      icon: Bell,
      label: 'Notifikasi',
      description: 'Atur preferensi notifikasi',
      visible: false, // Hidden - Task 008 requirement #4
    },
    {
      icon: Lock,
      label: t('profile.security'),
      description: t('profile.security_desc'),
      onClick: () => navigate('/profile/change-password'),
      visible: true,
    },
    {
      icon: HelpCircle,
      label: t('profile.help'),
      description: t('profile.help_desc'),
      onClick: () => window.open('https://fittory.sleekplan.app', '_blank'),
      visible: true,
    },
    {
      icon: MessageCircle,
      label: t('profile.connect_chatgpt'),
      description: t('profile.connect_chatgpt_desc'),
      onClick: () => navigate('/profile/connect-chatgpt'),
      visible: true,
    },
    {
      icon: FileText,
      label: 'Kebijakan Privasi',
      description: 'Baca kebijakan privasi kami',
      visible: false, // Hidden - Task 008 requirement #6
    },
  ];

  return (
    <div
      className="min-h-screen bg-[#ffffeb] pb-24 lg:pb-8"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      {/* Page Header */}
      <Header
        leftContent={
          <p
            className="text-left text-base font-bold uppercase hover:opacity-80 inline-block"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              transition: 'none',
              fontSize: '14px',
            }}
          >
            {t('profile.title')}
          </p>
        }
        maxWidth="max-w-lg"
      />

      <div className="px-6 py-6 max-w-lg lg:max-w-4xl mx-auto">
        <div
          className="lg:hidden bg-[#dffbd0] border-2 border-[#077f5d] p-6 mb-6"
          style={{ boxShadow: '3px 3px 0 #077f5d' }}
        >
          <div className="flex items-center gap-4">
            <div
              className="w-20 h-20 bg-[#3cbe8c] border-2 border-[#077f5d] flex items-center justify-center text-white text-2xl font-bold"
              style={{
                boxShadow: '2px 2px 0 #077f5d',
                fontFamily: "'IBM Plex Mono', monospace"
              }}
            >
              {user?.email ? getInitials(user.email) : 'U'}
            </div>
            <div className="flex-1">
              <h2
                className="text-xl font-bold text-[#1a1a1a] mb-1"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {user?.user_metadata?.name || user?.email?.split('@')[0] || 'User'}
              </h2>
              <p className="text-[#077f5d] text-sm font-mono">{user?.email}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {menuItems
            .filter((item) => item.visible !== false)
            .map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  onClick={item.onClick}
                  className="w-full bg-white border-2 border-[#077f5d] p-4 flex items-center gap-4 hover:bg-[#dffbd0]"
                  style={{
                    boxShadow: '3px 3px 0 #077f5d',
                    transition: 'none'
                  }}
                >
                  <div
                    className="w-12 h-12 bg-[#dffbd0] border-2 border-[#077f5d] flex items-center justify-center flex-shrink-0"
                    style={{ boxShadow: '2px 2px 0 #077f5d' }}
                  >
                    <Icon className="w-6 h-6 text-[#077f5d]" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-bold text-[#1a1a1a] mb-0.5 font-mono truncate">{item.label}</p>
                    <p className="text-sm text-[#077f5d] font-mono line-clamp-2">{item.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#077f5d] flex-shrink-0" />
                </button>
              );
            })}
        </div>

        <button
          onClick={handleSignOut}
          className="w-full bg-white border-2 border-[#077f5d] p-4 flex items-center gap-4 text-[#077f5d] hover:bg-[#dffbd0]"
          style={{
            boxShadow: '3px 3px 0 #077f5d',
            transition: 'none'
          }}
        >
          <div
            className="w-12 h-12 bg-[#dffbd0] border-2 border-[#077f5d] flex items-center justify-center"
            style={{ boxShadow: '2px 2px 0 #077f5d' }}
          >
            <LogOut className="w-6 h-6" />
          </div>
          <span className="flex-1 text-left font-bold font-mono text-[#1a1a1a]">{t('profile.sign_out')}</span>
          <ChevronRight className="w-5 h-5 text-[#077f5d]" />
        </button>

        {/* Debug Info */}
        <div
          className="mt-6 bg-white border-2 border-[#077f5d] p-4"
          style={{ boxShadow: '3px 3px 0 #077f5d' }}
        >
          <h3
            className="text-sm font-bold text-[#077f5d] mb-2 uppercase"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {t('profile.debug_info')}
          </h3>
          <div className="space-y-1 text-xs text-[#1a1a1a] font-mono">
            <p><span className="font-bold">{t('profile.user_id')}:</span> {user?.id || 'N/A'}</p>
            <p><span className="font-bold">{t('profile.email')}:</span> {user?.email || 'N/A'}</p>
            <button
              onClick={() => {
                if (user?.id) {
                  navigator.clipboard.writeText(user.id);
                  alert(t('profile.copied'));
                }
              }}
              className="mt-2 text-[#077f5d] font-bold hover:text-[#2da877]"
              style={{ transition: 'none' }}
            >
              {t('profile.copy_user_id')}
            </button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-[#077f5d] text-sm font-mono">Fittory v1.0.0</p>
          <p className="text-[#077f5d] text-xs mt-1 font-mono">© 2025 Fittory. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
